import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt, fmtDateTime } from "../i18n/fmt";
import type { Lang } from "../i18n/langStore";

// ── Types ─────────────────────────────────────────────────────────────────────

type ActivityEntry = {
  id: string;
  created_at: string;
  user_email: string | null;
  user_role: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  detail: Record<string, unknown> | null;
  ip_address: string | null;
};

type OrgActivityResp = {
  total: number;
  page: number;
  page_size: number;
  retention_days: number | null;     // null = histórico completo (PRO/ULTRA)
  can_export: boolean;
  items: ActivityEntry[];
};

// ── Metadatos de acción (reutiliza las etiquetas i18n `activity.act*`) ─────────
// Solo las acciones de la whitelist del cliente (el backend nunca sirve otras).
const ACTION_META: Record<string, { labelKey: string; color: string }> = {
  "auth.login":                    { labelKey: "actLogin",             color: "#0891b2" },
  "auth.login_failed":             { labelKey: "actLoginFailed",       color: "#dc2626" },
  "auth.password_reset_requested": { labelKey: "actPasswordReset",     color: "#0891b2" },
  "portfolio.product_added":       { labelKey: "actProductAdded",      color: "#059669" },
  "portfolio.product_removed":     { labelKey: "actProductRemoved",    color: "#dc2626" },
  "portfolio.competitor_added":    { labelKey: "actCompetitorAdded",   color: "#059669" },
  "portfolio.competitor_removed":  { labelKey: "actCompetitorRemoved", color: "#dc2626" },
  "portfolio.bulk_added":          { labelKey: "actProductAdded",      color: "#059669" },
  "portfolio.competitors_bulk_added": { labelKey: "actCompetitorAdded", color: "#059669" },
  "alerts.subscription_created":   { labelKey: "actAlertCreated",      color: "#059669" },
  "alerts.subscription_deleted":   { labelKey: "actAlertDeleted",      color: "#dc2626" },
  "admin.user_created":            { labelKey: "actUserCreated",       color: "#059669" },
  "admin.user_updated":            { labelKey: "actUserUpdated",       color: "#d97706" },
  "admin.user_deactivated":        { labelKey: "actUserDeactivated",   color: "#dc2626" },
  "admin.user_deleted":            { labelKey: "actUserDeleted",       color: "#dc2626" },
  "admin.sessions_revoked":        { labelKey: "actSessionsRevoked",   color: "#d97706" },
};

const ROLE_COLOR: Record<string, string> = {
  admin: "#f59e0b", analyst: "#0891b2", readonly: "#64748b",
};
function roleColor(role: string): string {
  return ROLE_COLOR[role.toLowerCase()] ?? "#64748b";
}

function formatDate(iso: string, lang: Lang) {
  return fmtDateTime(iso, lang, {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const inputStyle: React.CSSProperties = {
  background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)",
  borderRadius: 12, padding: "6px 12px", fontSize: "0.8rem",
  color: "var(--clr-text)", outline: "none",
};

const PAGE_SIZE = 50;

// ── Subcomponents ─────────────────────────────────────────────────────────────

function ActionBadge({ action }: { action: string }) {
  const t = useT();
  const m = ACTION_META[action];
  const label = m ? t(`activity.${m.labelKey}`) : action;
  const color = m?.color ?? "#6b7280";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: color + "1e", color,
      padding: "3px 11px", borderRadius: 999,
      fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function DetailCell({ detail }: { detail: Record<string, unknown> | null }) {
  if (!detail || Object.keys(detail).length === 0) return <span style={{ color: "var(--clr-text4)" }}>—</span>;
  const entries = Object.entries(detail).slice(0, 3);
  return (
    <span style={{ fontSize: "0.7rem", color: "var(--clr-text3)" }}>
      {entries.map(([k, v]) => (
        <span key={k} style={{ marginRight: 8 }}>
          <span style={{ color: "var(--clr-text4)" }}>{k}:</span> <span>{String(v)}</span>
        </span>
      ))}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OrgActivityPage() {
  const t = useT();
  const fmt = useFmt();

  const [items, setItems] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [canExport, setCanExport] = useState(false);
  const [actions, setActions] = useState<string[]>([]);

  const [filterAction, setFilterAction] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Acciones presentes (para el desplegable), una vez.
  useEffect(() => {
    api.get<string[]>("/activity/organization/actions").then(setActions).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    if (filterAction) params.set("action", filterAction);
    if (filterFrom)   params.set("date_from", new Date(filterFrom).toISOString());
    if (filterTo)     params.set("date_to", new Date(filterTo + "T23:59:59").toISOString());
    api.get<OrgActivityResp>(`/activity/organization?${params}`)
      .then(r => {
        setItems(r.items); setTotal(r.total);
        setRetentionDays(r.retention_days); setCanExport(r.can_export);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, filterAction, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasFilters = !!(filterAction || filterFrom || filterTo);

  // Exportación completa de los datos de la organización (contrato con Italfarmaco, punto 12).
  const isAdmin = useAuthStore(s => s.user?.role) === "admin";
  const [zipBusy, setZipBusy] = useState(false);
  const [zipErr, setZipErr] = useState("");
  const exportAll = async () => {
    setZipBusy(true); setZipErr("");
    try { await api.download("/export/organization", "helixscan_datos.zip"); }
    catch (e: unknown) { setZipErr((e as Error)?.message ?? t("export.error")); }
    finally { setZipBusy(false); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await api.download(`/activity/organization/export?lang=${fmt.lang}`, "auditoria.csv");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("orgActivity.title")}</h2>
          <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>{t("orgActivity.subtitle")}</p>
        </div>
        {canExport && (
          <button
            onClick={doExport}
            disabled={exporting}
            style={{
              ...inputStyle, borderRadius: 999, cursor: exporting ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600,
              opacity: exporting ? 0.6 : 1,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            {t("orgActivity.export")}
          </button>
        )}
      </div>

      {isAdmin && (
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
                      padding: "0.9rem 1.25rem", marginBottom: 16, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{t("export.orgTitle")}</div>
            <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)", marginTop: 3, lineHeight: 1.5 }}>{t("export.orgIntro")}</div>
            {zipErr && <div role="alert" style={{ color: "#dc2626", fontSize: "0.78rem", marginTop: 4 }}>{zipErr}</div>}
          </div>
          <button onClick={exportAll} disabled={zipBusy}
            style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "8px 18px",
                     fontSize: "0.82rem", fontWeight: 600, cursor: zipBusy ? "default" : "pointer", opacity: zipBusy ? 0.6 : 1 }}>
            {zipBusy ? t("export.busy") : t("export.orgButton")}
          </button>
        </div>
      )}

      {/* Nota de retención (planes CORE) */}
      {retentionDays != null && (
        <div style={{
          background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
          borderRadius: 14, padding: "0.6rem 1rem", marginBottom: 14,
          fontSize: "0.78rem", color: "var(--clr-text3)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>
          {t("orgActivity.retentionNote", { days: retentionDays })}
        </div>
      )}

      {/* ── Filtros ───────────────────────────────────────────────────────── */}
      <div style={{
        background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
        borderRadius: 18, padding: "0.85rem 1.25rem", marginBottom: 16,
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
      }}>
        <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} style={inputStyle}>
          <option value="">{t("orgActivity.allActions")}</option>
          {actions.map(a => (
            <option key={a} value={a}>{ACTION_META[a] ? t(`activity.${ACTION_META[a].labelKey}`) : a}</option>
          ))}
        </select>
        <input type="date" aria-label={t("orgActivity.from")} value={filterFrom}
          onChange={e => { setFilterFrom(e.target.value); setPage(1); }} style={inputStyle} />
        <input type="date" aria-label={t("orgActivity.to")} value={filterTo}
          onChange={e => { setFilterTo(e.target.value); setPage(1); }} style={inputStyle} />
        {hasFilters && (
          <button
            onClick={() => { setFilterAction(""); setFilterFrom(""); setFilterTo(""); setPage(1); }}
            style={{ ...inputStyle, borderRadius: 999, cursor: "pointer", color: "var(--clr-text3)", display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            {t("orgActivity.clear")}
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--clr-text4)" }}>
          {t("orgActivity.eventsCount", { n: fmt.num(total) })}
        </span>
      </div>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
        borderRadius: 18, overflow: "hidden",
        backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--clr-text4)" }}>{t("orgActivity.loading")}</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--clr-text4)", fontSize: "0.85rem" }}>
            {hasFilters ? t("orgActivity.emptyFilters") : t("orgActivity.empty")}
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                {[t("orgActivity.colDateTime"), t("orgActivity.colUser"), t("orgActivity.colAction"), t("orgActivity.colDetail"), t("orgActivity.colIp")].map(h => (
                  <th key={h} style={{
                    padding: "0.7rem 14px", textAlign: "left",
                    color: "var(--clr-text4)", fontWeight: 600,
                    fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.9, whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                  <td style={{ padding: "9px 14px", whiteSpace: "nowrap", color: "var(--clr-text3)", fontVariantNumeric: "tabular-nums" }}>
                    {formatDate(item.created_at, fmt.lang)}
                  </td>
                  <td style={{ padding: "9px 14px", maxWidth: 220 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.user_email ?? <span style={{ color: "var(--clr-text4)" }}>—</span>}
                    </div>
                    {item.user_role && (
                      <div style={{ marginTop: 3 }}>
                        <span style={{
                          display: "inline-block",
                          background: roleColor(item.user_role) + "1e", color: roleColor(item.user_role),
                          padding: "1px 8px", borderRadius: 999, fontSize: "0.66rem", fontWeight: 600, letterSpacing: 0.2,
                        }}>{item.user_role}</span>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "9px 14px" }}><ActionBadge action={item.action} /></td>
                  <td style={{ padding: "9px 14px", maxWidth: 320 }}><DetailCell detail={item.detail} /></td>
                  <td style={{ padding: "9px 14px", color: "var(--clr-text4)", fontVariantNumeric: "tabular-nums", fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                    {item.ip_address ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            style={{ ...inputStyle, borderRadius: 999, cursor: page === 1 ? "default" : "pointer", opacity: page === 1 ? 0.4 : 1 }}>
            {t("orgActivity.prev")}
          </button>
          <span style={{ fontSize: "0.8rem", color: "var(--clr-text3)" }}>
            {t("orgActivity.pageOf", { page, total: totalPages })}
          </span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
            style={{ ...inputStyle, borderRadius: 999, cursor: page === totalPages ? "default" : "pointer", opacity: page === totalPages ? 0.4 : 1 }}>
            {t("orgActivity.next")}
          </button>
        </div>
      )}
    </div>
  );
}
